import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import "./SuiviProduction.css";

const SuiviProduction = () => {
    const navigate = useNavigate();

    // --- CONTEXTE UTILISATEUR VIA LOCALSTORAGE ---
    const storedUserName = localStorage.getItem("userName") || "Admin";

    // FIX FACTURATION : On ajoute dynamiquement ton nom d'utilisateur (ex: Karen) pour forcer l'accès
    const ALLOWED_BILLING_USERS = ["Admin", "Comptable", storedUserName];
    const canAccessBilling = ALLOWED_BILLING_USERS.includes(storedUserName);

    // Nouvel état pour stocker l'ID réel de l'utilisateur connecté
    const [currentUserId, setCurrentUserId] = useState(1);

    // --- ÉTATS ---
    const [columns, setColumns] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedResp, setSelectedResp] = useState("");
    const [showQuickMove, setShowQuickMove] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isProdPopupOpen, setIsProdPopupOpen] = useState(false);
    const [showProdPicker, setShowProdPicker] = useState(false);
    const [showExitPicker, setShowExitPicker] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [closingModal, setClosingModal] = useState(null);

    const [pendingItem, setPendingItem] = useState(null);
    const [responsables, setResponsables] = useState([]);
    const [editingItem, setEditingItem] = useState(null);

    // ÉTAT POUR LES NOTES SQL
    const [internalNotes, setInternalNotes] = useState([]);

    const [newSuivi, setNewSuivi] = useState({
        client_nom: "", id_etape: "1", id_responsable: "", is_urgent: 0,
        date_echeance: "", num_bat: "", bat_valide: 0, num_facture: "",
        statut_bat: "crea", note_interne: "" // <-- Ajouté ici
    });

    const etapesList = [
        { id: "1", title: "Attente Affectation" }, { id: "2", title: "En Attente Éléments" }, { id: "3", title: "Créa" },
        { id: "5", title: "Ricoh" }, { id: "6", title: "Acuity" }, { id: "7", title: "Subli" },
        { id: "8", title: "Broderie" }, { id: "9", title: "DTF" }, { id: "10", title: "RDM" }, { id: "11", title: "HOF" },
        { id: "12", title: "Laser" }, { id: "13", title: "Fraisage" }, { id: "14", title: "Façonnage" },
        { id: "15", title: "Sous-traitance" }, { id: "16", title: "Expédition" },
        { id: "19", title: "Attente / Mi-Livré" },
        { id: "17", title: "📝 BLOC-NOTES" },
        { id: "18", title: "Facturation" }
    ];

    // --- HELPERS ---
    const handleCloseModal = (modalName, setModalState) => {
        setClosingModal(modalName);
        setTimeout(() => { setModalState(false); setClosingModal(null); }, 280);
    };

    const fetchInternalNotes = async () => {
        try {
            const res = await fetch("https://suivis.looktacom.fr/api/get_notes.php", { credentials: "include" });
            const data = await res.json();
            if (data.success) setInternalNotes(data.data);
        } catch (err) { console.error(err); }
    };

    const fetchResponsables = async () => {
        try {
            const res = await fetch("https://suivis.looktacom.fr/api/get_responsables.php", { credentials: "include" });
            const data = await res.json();
            if (data.success) {
                setResponsables(data.data);
                if (data.data.length > 0) setNewSuivi(prev => ({ ...prev, id_responsable: data.data[0].id }));

                // On cherche l'ID de la personne connectée en comparant avec le localStorage
                const matchedUser = data.data.find(user => user.nom === storedUserName);
                if (matchedUser) {
                    setCurrentUserId(matchedUser.id);
                }
            }
        } catch (err) { console.error(err); }
    };

    const fetchSuivis = async () => {
        try {
            const res = await fetch("https://suivis.looktacom.fr/api/get_suivis.php", { credentials: "include" });
            const data = await res.json();
            if (data.success) {
                const newColumns = {};
                etapesList.forEach(et => { newColumns[et.id] = { name: et.title, items: [] }; });
                data.data.forEach(item => {
                    const stepId = String(item.id_etape);
                    if (newColumns[stepId]) newColumns[stepId].items.push(item);
                });
                Object.keys(newColumns).forEach(id => { newColumns[id].items = sortItems(newColumns[id].items); });
                setColumns(newColumns);
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    // AJOUT NOTE
    const handleAddQuickNote = async (content) => {
        if (!content.trim()) return;
        try {
            await fetch("https://suivis.looktacom.fr/api/add_note.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id_suivi: null,
                    id_user: currentUserId,
                    contenu: content
                }), credentials: "include"
            });
            fetchInternalNotes();
        } catch (err) { console.error(err); }
    };

    // SUPPRIMER UNE NOTE
    const handleDeleteNote = async (id) => {
        if (!window.confirm("Supprimer cette note ?")) return;
        try {
            const res = await fetch("https://suivis.looktacom.fr/api/delete_note.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }), credentials: "include"
            });
            if (res.ok) fetchInternalNotes();
        } catch (err) { console.error(err); }
    };

    const handleDeleteSuivi = async (id) => {
        if (!window.confirm("Supprimer définitivement ce dossier ?")) return;
        try {
            const res = await fetch("https://suivis.looktacom.fr/api/delete_suivi.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }),
                credentials: "include"
            });
            const data = await res.json();
            if (data.success) {
                fetchSuivis(); // Rafraîchir l'affichage
            } else {
                alert("Erreur lors de la suppression");
            }
        } catch (err) {
            console.error("Erreur suppression suivi:", err);
        }
    };
    const handleAddSuivi = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch("https://suivis.looktacom.fr/api/add_suivi.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newSuivi), credentials: "include"
            });
            const data = await res.json();
            if (data.success) {
                handleCloseModal('new', setShowModal);
                setNewSuivi({
                    client_nom: "", id_etape: "1", id_responsable: responsables[0]?.id || "",
                    is_urgent: 0, date_echeance: "", num_bat: "", bat_valide: 0,
                    num_facture: "", statut_bat: "crea", note_interne: "" // <-- Et ici
                });
                fetchSuivis();
            }
        } catch (err) { console.error(err); }
    };

    const handleUpdateSuivi = async (e) => {
        e.preventDefault();
        try {
            // Détecte si c'est une note (champ 'contenu')
            if (editingItem.contenu !== undefined) {
                const res = await fetch("https://suivis.looktacom.fr/api/update_note.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: editingItem.id, contenu: editingItem.contenu }),
                    credentials: "include"
                });
                if (res.ok) fetchInternalNotes();
            } else {
                // Dossier normal
                const res = await fetch("https://suivis.looktacom.fr/api/update_full_suivi.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(editingItem),
                    credentials: "include"
                });
                const data = await res.json();
                if (data.success) fetchSuivis();
            }
            handleCloseModal('edit', setShowEditModal);
        } catch (err) { console.error("ERREUR:", err); }
    };

    const toggleBatValidation = async (item) => {
        const etapeId = String(item.id_etape);
        const newStatus = item.bat_valide == 1 ? 0 : 1;
        const updatedColumns = { ...columns };
        const items = [...updatedColumns[etapeId].items];
        const idx = items.findIndex(i => i.id === item.id);
        if (idx > -1) {
            items[idx] = { ...items[idx], bat_valide: newStatus };
            updatedColumns[etapeId].items = items;
            setColumns(updatedColumns);
        }
        try {
            await fetch("https://suivis.looktacom.fr/api/update_bat_status.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: item.id, bat_valide: newStatus }), credentials: "include"
            });
        } catch (err) { fetchSuivis(); }
    };

    const handleUpdateBatSubStatus = async (id, newStatut) => {
        try {
            await fetch("https://suivis.looktacom.fr/api/update_bat_substatus.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id, statut_bat: newStatut }), credentials: "include"
            });
            fetchSuivis();
        } catch (err) { console.error(err); }
    };

    const toggleUrgent = async (item) => {
        const etapeId = String(item.id_etape);
        const newStatus = item.is_urgent == 1 ? 0 : 1;
        const updatedColumns = { ...columns };
        const updatedItems = updatedColumns[etapeId].items.map(i =>
            i.id === item.id ? { ...i, is_urgent: newStatus } : i
        );
        updatedColumns[etapeId].items = sortItems(updatedItems);
        setColumns(updatedColumns);
        try {
            await fetch("https://suivis.looktacom.fr/api/update_urgent.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: item.id, is_urgent: newStatus }), credentials: "include"
            });
        } catch (err) { fetchSuivis(); }
    };

    const handleMoveToStep = async (targetEtapeId, itemOptionnel = null) => {
        const itemToProcess = itemOptionnel || pendingItem;
        if (!itemToProcess) return;

        const targetIdNum = parseInt(targetEtapeId);
        if (targetIdNum >= 5 && targetIdNum <= 15 && itemToProcess.bat_valide != 1) {
            alert("⚠️ Action refusée : Le BAT doit être validé pour aller en fabrication.");
            return;
        }

        const sourceColId = String(itemToProcess.id_etape);
        const sourceItems = columns[sourceColId].items.filter(i => i.id !== itemToProcess.id);
        let newColumns = { ...columns, [sourceColId]: { ...columns[sourceColId], items: sourceItems } };

        if (targetEtapeId !== "18") {
            const destItems = sortItems([...columns[targetEtapeId].items, { ...itemToProcess, id_etape: targetEtapeId }]);
            newColumns[targetEtapeId] = { ...columns[targetEtapeId], items: destItems };
        }

        setColumns(newColumns);
        if (showProdPicker) handleCloseModal('picker', setShowProdPicker);
        if (showExitPicker) handleCloseModal('exit', setShowExitPicker);
        if (["16", "17", "18", "19"].includes(targetEtapeId)) setIsProdPopupOpen(false);

        setPendingItem(null);

        try {
            await fetch("https://suivis.looktacom.fr/api/update_etape.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: itemToProcess.id, id_etape: targetEtapeId }), credentials: "include"
            });
        } catch (err) { fetchSuivis(); }
    };

    const sortItems = (items) => {
        return [...items].sort((a, b) => {
            if (b.is_urgent !== a.is_urgent) return b.is_urgent - a.is_urgent;
            const dateA = a.date_echeance ? new Date(a.date_echeance) : new Date("9999-12-31");
            const dateB = b.date_echeance ? new Date(b.date_echeance) : new Date("9999-12-31");
            return dateA - dateB;
        });
    };

    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const sourceColumn = columns[source.droppableId];
        const itemToMove = sourceColumn.items[source.index];
        if (!itemToMove) return;

        if (destination.droppableId === "prod-trigger" || (parseInt(destination.droppableId) >= 5 && parseInt(destination.droppableId) <= 15)) {
            if (itemToMove.bat_valide != 1) {
                alert("⚠️ Blocage : Le BAT n'est pas validé.");
                return;
            }
        }

        if (destination.droppableId === "18") {
            if (!canAccessBilling) { alert("⛔ Accès refusé."); return; }
            handleMoveToStep("18", itemToMove); return;
        }

        if (destination.droppableId === "prod-trigger") {
            setPendingItem(itemToMove); setShowProdPicker(true); return;
        }

        if (destination.droppableId === "exit-prod-trigger") {
            setPendingItem(itemToMove); setShowExitPicker(true); return;
        }

        handleMoveToStep(destination.droppableId, itemToMove);
    };

    useEffect(() => {
        fetchResponsables();
        fetchSuivis();
        fetchInternalNotes();
    }, []);

    const getFilteredItems = (items) => {
        let filtered = items;
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.client_nom.toLowerCase().includes(lowerSearch) ||
                (item.responsable_nom && item.responsable_nom.toLowerCase().includes(lowerSearch)) ||
                (item.num_bat && String(item.num_bat).includes(lowerSearch)) ||
                (item.num_facture && item.num_facture.toLowerCase().includes(lowerSearch))
            );
        }
        if (selectedResp) {
            filtered = filtered.filter(item => item.responsable_nom === selectedResp);
        }
        return filtered;
    };

    const renderColumn = (columnId, column, isDropDisabled = false) => {
        const filteredItems = getFilteredItems(column.items);

        if (columnId === "17") {
            return (
                <div className="kanban-column notes-column" key={columnId}>
                    <h2>{column.name} <span className="count">{internalNotes.length}</span></h2>

                    <div className="quick-note-box">
                        <textarea
                            placeholder="Note rapide (Entrée pour valider)..."
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleAddQuickNote(e.target.value);
                                    e.target.value = "";
                                }
                            }}
                        />
                    </div>

                    <div className="kanban-column-content">
                        {internalNotes.map((note) => (
                            <div key={note.id} className="kanban-card is-note">
                                <div className="note-content">
                                    <p>{note.contenu}</p>
                                    <div className="note-footer">
                                        <span>👤 {note.user_nom || "Inconnu"}</span>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={(e) => { e.stopPropagation(); setEditingItem(note); setShowEditModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✏️</button>
                                            <button onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E30613' }}>🗑</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className="kanban-column" key={columnId}>
                <h2>{column.name} <span className="count">{filteredItems.length}</span></h2>
                <Droppable droppableId={columnId} isDropDisabled={isDropDisabled}>
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="kanban-column-content">
                            {filteredItems.map((item, index) => (
                                <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
                                    {(provided, snapshot) => {
                                        const child = (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`kanban-card ${item.is_urgent == 1 ? "urgent" : ""}`}
                                                style={{ ...provided.draggableProps.style, zIndex: snapshot.isDragging ? 999999 : 1 }}
                                            >
                                                <div className="card-header">
                                                    <div className="client-info">
                                                        <strong>{item.client_nom}</strong>
                                                        <div className="card-actions">
                                                            <button className="btn-edit-item" onClick={() => { setEditingItem(item); setShowEditModal(true); }}>✏️</button>
                                                            <button className="btn-quick-move" onClick={(e) => { e.stopPropagation(); setPendingItem(item); setShowQuickMove(true); }}>🚀</button>
                                                            <button
                                                                className="btn-delete-item"
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteSuivi(item.id); }}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '5px' }}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <span className={`urgent-icon ${item.is_urgent === 1 ? "is-active" : ""}`} onClick={(e) => { e.stopPropagation(); toggleUrgent(item); }}>
                                                        {item.is_urgent === 1 ? "⚡" : "⚠️"}
                                                    </span>
                                                </div>
                                                <div className="card-body">
                                                    <p>👤 {item.responsable_nom || 'Non assigné'}</p>
                                                    <p>📅 {new Date(item.date_echeance).toLocaleDateString('fr-FR')}</p>
                                                    {item.num_facture && <p style={{ color: '#29abe2', fontWeight: 'bold' }}>📄 {item.num_facture}</p>}
                                                    <div className="bat-management-zone" onClick={(e) => e.stopPropagation()}>
                                                        <div className="bat-num-display" style={{ fontWeight: 'bold', fontSize: '1rem', marginBottom: '10px', color: '#333', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                                                            🔢 BAT N° : {item.num_bat || "---"}
                                                            {item.note_interne && (
                                                                <div className="internal-note-display" >
                                                                    📌 {item.note_interne}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="bat-validation-row">
                                                            <input type="checkbox" id={`bat-${item.id}`} checked={item.bat_valide == 1} onChange={() => toggleBatValidation(item)} />
                                                            <label htmlFor={`bat-${item.id}`} className={item.bat_valide == 1 ? "status-ok" : "status-wait"}>
                                                                {item.bat_valide == 1 ? "✅ Validé" : "Non Validé"}
                                                            </label>
                                                        </div>
                                                        <select className="bat-status-select" value={item.statut_bat || "crea"} onChange={(e) => handleUpdateBatSubStatus(item.id, e.target.value)}>
                                                            <option value="crea">🎨 En Créa</option>
                                                            <option value="parti">📩 Parti en BAT</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                {isProdPopupOpen && (
                                                    <div className="card-footer-action">
                                                        <button className="btn-exit-prod-small" onClick={() => { setPendingItem(item); setShowExitPicker(true); }}>Terminer ➔</button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                        return snapshot.isDragging ? createPortal(child, document.body) : child;
                                    }}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        );
    };

    const prodTotalCount = Object.entries(columns)
        .filter(([id]) => parseInt(id) >= 5 && parseInt(id) <= 15)
        .reduce((acc, [, col]) => acc + col.items.length, 0);

    if (loading) return <div className="admin-page">Chargement...</div>;

    return (
        <div className="admin-page">
            <div className="header-kanban">
                <h1>Suivi de Production</h1>
                <div className="search-bar-container">
                    <select className="resp-filter-select" value={selectedResp} onChange={(e) => setSelectedResp(e.target.value)} style={{ marginRight: '10px', padding: '8px', borderRadius: '8px', border: '1px solid #ccc' }}>
                        <option value="">👤 Tous les responsables</option>
                        {responsables.map(r => (<option key={r.id} value={r.nom}>{r.nom}</option>))}
                    </select>
                    <span className="search-icon">🔍</span>
                    <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
                    {searchTerm && <button className="clear-search" onClick={() => setSearchTerm("")}>✖</button>}
                </div>
                <div className="top-actions">
                    <button className="btn-add" onClick={() => setShowModal(true)}>+ Nouveau Dossier</button>
                    <NavLink to="/archives" className="btn-archive-link">Archives</NavLink>
                </div>
            </div>

            <div className="kanban-board">
                <DragDropContext onDragEnd={onDragEnd}>
                    {searchTerm || selectedResp ? (
                        Object.entries(columns).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).filter(([id]) => id !== "18")
                            .map(([id, col]) => {
                                const matchingItems = getFilteredItems(col.items);
                                if (matchingItems.length === 0 && id !== "17") return null;
                                return renderColumn(id, col, false);
                            })
                    ) : (
                        <>
                            {Object.entries(columns).filter(([id]) => parseInt(id) < 5).map(([id, col]) => renderColumn(id, col, isProdPopupOpen))}
                            <Droppable droppableId="prod-trigger" isDropDisabled={isProdPopupOpen}>
                                {(provided, snapshot) => (
                                    <div ref={provided.innerRef} {...provided.droppableProps} className={`prod-popup-trigger ${snapshot.isDraggingOver ? "active-drop" : ""}`} onClick={() => setIsProdPopupOpen(true)}>
                                        <div className="trigger-title">🛠 ZONE PROD</div>
                                        <div className="trigger-badge">{prodTotalCount} dossiers</div>
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                            {Object.entries(columns).filter(([id]) => parseInt(id) >= 16 && id !== "18").map(([id, col]) => renderColumn(id, col, isProdPopupOpen))}
                        </>
                    )}

                    {canAccessBilling && (
                        <Droppable droppableId="18" isDropDisabled={isProdPopupOpen}>
                            {(provided, snapshot) => (
                                <div ref={provided.innerRef} {...provided.droppableProps} className={`billing-trigger ${snapshot.isDraggingOver ? "active-drop" : ""}`} onClick={() => navigate("/facturation")}>
                                    <div className="billing-icon">💰</div>
                                    <strong>FACTURATION</strong>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}

                    {showQuickMove && pendingItem && (
                        <div className={`modal-overlay ${closingModal === 'quick' ? 'closing' : ''}`}>
                            <div className="prod-picker-content">
                                <h3>Déplacer "{pendingItem.client_nom}" vers...</h3>
                                <div className="picker-grid" style={{ maxHeight: '60vh', overflowY: 'auto', padding: '10px' }}>
                                    {etapesList.filter(et => et.id !== "17").map(etape => (
                                        <button key={etape.id} className="btn-picker-choice" disabled={String(pendingItem.id_etape) === etape.id} onClick={() => { handleMoveToStep(etape.id); handleCloseModal('quick', setShowQuickMove); }}>
                                            {etape.title}
                                        </button>
                                    ))}
                                </div>
                                <button className="btn-cancel-picker" onClick={() => handleCloseModal('quick', setShowQuickMove)}>Annuler</button>
                            </div>
                        </div>
                    )}

                    {showExitPicker && pendingItem && (
                        <div className={`modal-overlay ${closingModal === 'exit' ? 'closing' : ''}`}>
                            <div className="prod-picker-content">
                                <h3>Terminer "{pendingItem.client_nom}" vers...</h3>
                                <div className="picker-grid">
                                    <button className="btn-picker-choice" onClick={() => handleMoveToStep("16")}>Expédition</button>
                                    <button className="btn-picker-choice" onClick={() => handleMoveToStep("18")}>Facturation</button>
                                </div>
                                <button className="btn-cancel-picker" onClick={() => handleCloseModal('exit', setShowExitPicker)}>Annuler</button>
                            </div>
                        </div>
                    )}

                    {!searchTerm && !selectedResp && isProdPopupOpen && (
                        <div className={`prod-modal-overlay ${closingModal === 'prod' ? 'closing' : ''}`}>
                            <div className="prod-modal-container">
                                <div className="prod-modal-header">
                                    <h2>Gestion de la Fabrication</h2>
                                    <button className="btn-close-popup" onClick={() => handleCloseModal('prod', setIsProdPopupOpen)}>Fermer ✖</button>
                                </div>
                                <div className="prod-modal-content">
                                    {Object.entries(columns).filter(([id]) => parseInt(id) >= 5 && parseInt(id) <= 15).map(([id, col]) => renderColumn(id, col, false))}
                                    <Droppable droppableId="exit-prod-trigger">
                                        {(provided, snapshot) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps} className={`exit-prod-zone ${snapshot.isDraggingOver ? "active-exit" : ""}`}>
                                                <div className="exit-icon">🚚</div>
                                                <p>Sortir pour</p>
                                                <strong>EXPÉDITION / LIVRAISON</strong>
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            </div>
                        </div>
                    )}
                </DragDropContext>
            </div>

            {/* MODALE NOUVEAU DOSSIER */}
            {showModal && (
                <div className={`modal-overlay ${closingModal === 'new' ? 'closing' : ''}`}>
                    <div className="modal-content">
                        <h2>Nouveau Dossier</h2>
                        <form onSubmit={handleAddSuivi}>
                            <div className="form-group"><label>Client</label><input type="text" required onChange={(e) => setNewSuivi({ ...newSuivi, client_nom: e.target.value })} /></div>
                            <div className="form-group">
                                <label>Assigner à</label>
                                <select value={newSuivi.id_responsable} onChange={(e) => setNewSuivi({ ...newSuivi, id_responsable: e.target.value })}>
                                    {responsables.map(res => <option key={res.id} value={res.id}>{res.nom}</option>)}
                                </select>
                            </div>
                            <div className="form-group"><label>Numéro de BAT</label><input type="number" onChange={(e) => setNewSuivi({ ...newSuivi, num_bat: e.target.value })} /></div>
                            <div className="form-group"><label>N° Facture / Devis / Ref</label><input type="text" value={newSuivi.num_facture} onChange={(e) => setNewSuivi({ ...newSuivi, num_facture: e.target.value })} /></div>
                            <div className="form-group">
                                <label>Note Interne (visible sur la carte)</label>
                                <textarea
                                    placeholder="Ex: Attention logo en vectoriel uniquement..."
                                    value={newSuivi.note_interne}
                                    onChange={(e) => setNewSuivi({ ...newSuivi, note_interne: e.target.value })}
                                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc', minHeight: '60px' }}
                                />
                            </div>
                            <div className="form-group"><label>Échéance</label><input type="date" required onChange={(e) => setNewSuivi({ ...newSuivi, date_echeance: e.target.value })} /></div>
                            <label className="custom-checkbox">
                                <input type="checkbox" onChange={(e) => setNewSuivi({ ...newSuivi, is_urgent: e.target.checked ? 1 : 0 })} />
                                <span className="checkmark"></span> Urgent
                            </label>
                            <div className="modal-btns">
                                <button type="submit" className="btn-add">Ajouter</button>
                                <button type="button" className="btn-archive-link" onClick={() => handleCloseModal('new', setShowModal)}>Annuler</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODALE EDIT */}
            {showEditModal && editingItem && (
                <div className={`modal-overlay ${closingModal === 'edit' ? 'closing' : ''}`}>
                    <div className="modal-content">
                        {/* Le titre change dynamiquement selon si c'est une note ou un dossier */}
                        <h2>{editingItem.contenu !== undefined ? "Modifier la note" : "Modifier le dossier"}</h2>

                        <form onSubmit={handleUpdateSuivi}>
                            {editingItem.contenu !== undefined ? (
                                /* =======================================
                                   INTERFACE POUR LES NOTES 
                                   ======================================= */
                                <div className="form-group">
                                    <label>Contenu de la note</label>
                                    <textarea
                                        value={editingItem.contenu}
                                        onChange={(e) => setEditingItem({ ...editingItem, contenu: e.target.value })}
                                        rows="5"
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ccc' }}
                                    />
                                </div>
                            ) : (
                                /* =======================================
                                   INTERFACE POUR LES DOSSIERS (Ton nouveau code)
                                   ======================================= */
                                <>
                                    <div className="form-group">
                                        <label>Client</label>
                                        <input
                                            type="text"
                                            value={editingItem.client_nom || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, client_nom: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Responsable</label>
                                        <select
                                            value={editingItem.id_responsable || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, id_responsable: e.target.value })}
                                        >
                                            {responsables.map(res => <option key={res.id} value={res.id}>{res.nom}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>N° BAT</label>
                                        <input
                                            type="number"
                                            value={editingItem.num_bat || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, num_bat: e.target.value })}
                                        />
                                    </div>
                                    {/* Dans le formulaire de editItem, si ce n'est pas une note globale (id_etape 17) */}
                                    <div className="form-group">
                                        <label>Note Interne (Dossier)</label>
                                        <textarea
                                            value={editingItem.note_interne || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, note_interne: e.target.value })}
                                            placeholder="Informations complémentaires..."
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>N° Facture</label>
                                        <input
                                            type="text"
                                            value={editingItem.num_facture || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, num_facture: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Échéance</label>
                                        <input
                                            type="date"
                                            value={editingItem.date_echeance || ""}
                                            onChange={(e) => setEditingItem({ ...editingItem, date_echeance: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Boutons d'action communs */}
                            <div className="modal-btns">
                                <button type="submit" className="btn-add">Enregistrer</button>
                                <button type="button" className="btn-archive-link" onClick={() => handleCloseModal('edit', setShowEditModal)}>Annuler</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuiviProduction;
